import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import { z } from 'zod';
import {
  ApplicationsIdSchema,
  ApplicationGitIntegrationIdSchema,
  GitRepositoriesReturnSchema,
  GitRepositoriesPagedReturnSchema,
  GitBranchesReturnSchema,
  GitBranchesRequestSchema,
  GitIntegrationsReturnSchema,
  GitRepositoriesRequestSchema,
  ApplicationRepositoryByNameSchema,
  GitRepositoryByNameReturnSchema,
  GitAggregatedMetricsSchema,
  ApplicationMetricsReturnSchema,
  GitEntryPointsReturnSchema,
  GitProcessedPullRequestsReturnSchema,
  GitIntegrationUpsertReturnSchema,
  GitEntryPointsQuerySchema,
  GitProcessedPullRequestsCountQuerySchema,
  GitProcessedPullRequestsQuerySchema,
  GitDateRangeQuerySchema,
  GitCountReturnSchema,
  GitIntegrationUpsertBodySchema,
  EndpointFilesReturnSchema,
  FileContentReturnSchema,
  EndpointCodeGraphReturnSchema,
  EndpointCodeGraphErrorSchema,
  ManualWebhookVerificationSchema,
  ManualWebhookVerificationResultSchema,
  ApplicationWebhookUrlReturnSchema,
  TenantGitIntegrationBodySchema,
  TenantGitIntegrationReturnSchema,
  TenantGitIntegrationsListReturnSchema,
  TenantGitIntegrationByIdReturnSchema,
  TenantGitIntegrationIdSchema,
  TenantGitIntegrationsQuerySchema,
  UpdateTenantGitIntegrationBodySchema,
  ApplicationGitIntegrationBodySchema,
  ApplicationGitIntegrationReturnSchema,
  GitOrganizationsRequestSchema,
  GitOrganizationsReturnSchema,
  AiFlowGraphReturnSchema,
  AiFlowNodeCodeGraphsReturnSchema,
  TransformZipIntegrationBodySchema,
  TransformZipIntegrationReturnSchema,
} from './git.schema';
import {
  CodeGraphWithRelationsSchema,
  GitIntegrationPlatformSchema,
} from '@terra/shared-schemas-git';
import { normalizeGitIntegrationBaseUrl } from './normalize-git-integration-base-url';
import { gitAutomationContract } from './git-automation.contract';
import { gitGitHubEnterpriseContract } from './git-github-enterprise.contract';
import { gitSourceCodeUploadContract } from './git-source-code-upload.contract';

export const gitContract = terraContract.router({
  ...gitAutomationContract,
  ...gitGitHubEnterpriseContract,
  ...gitSourceCodeUploadContract,
  createTenantGitIntegration: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/tenant/git-integration',
      summary: 'Create tenant-level git integration',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: TenantGitIntegrationBodySchema }))
    .output(TenantGitIntegrationReturnSchema)
    .errors({
      FORBIDDEN: { status: 403, data: z.object({ message: z.string() }) },
      CONFLICT: { status: 409, data: z.object({ message: z.string() }) },
      INTERNAL_SERVER_ERROR: {
        status: 500,
        data: z.object({ message: z.string() }),
      },
    }),
  getTenantGitIntegrations: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/tenant/git-integrations',
      summary: 'List all tenant-level git integrations',
      inputStructure: 'detailed',
    })
    .input(z.object({ query: TenantGitIntegrationsQuerySchema }))
    .output(TenantGitIntegrationsListReturnSchema),
  getTenantGitIntegrationById: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/tenant/git-integration/{integrationId}',
      summary: 'Get tenant-level git integration by id',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: TenantGitIntegrationIdSchema }))
    .output(TenantGitIntegrationByIdReturnSchema)
    .errors({
      NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
    }),
  updateTenantGitIntegration: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'PATCH',
      path: '/tenant/git-integration/{integrationId}',
      summary: 'Update tenant-level git integration metadata',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: TenantGitIntegrationIdSchema,
        body: UpdateTenantGitIntegrationBodySchema,
      }),
    )
    .output(z.object({ success: z.boolean() }))
    .errors({
      NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      CONFLICT: { status: 409, data: z.object({ message: z.string() }) },
      BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
      INTERNAL_SERVER_ERROR: {
        status: 500,
        data: z.object({ message: z.string() }),
      },
    }),
  getTenantGitIntegrationWebhookData: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/tenant/git-integration/{integrationId}/webhook-data',
      summary: 'Get webhook URL and decoded secret for tenant git integration',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: TenantGitIntegrationIdSchema }))
    .output(ApplicationWebhookUrlReturnSchema),
  deleteTenantGitIntegration: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'DELETE',
      path: '/tenant/git-integration/{integrationId}',
      summary:
        'Delete tenant-level git integration (cascades to application bindings)',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: TenantGitIntegrationIdSchema }))
    .output(z.object({ success: z.boolean() }))
    .errors({
      NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      INTERNAL_SERVER_ERROR: {
        status: 500,
        data: z.object({ message: z.string() }),
      },
    }),
  createApplicationGitIntegration: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/{applicationId}/application-git-integration',
      summary:
        'Create application git integration binding to tenant integration',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationsIdSchema,
        body: ApplicationGitIntegrationBodySchema,
      }),
    )
    .output(ApplicationGitIntegrationReturnSchema)
    .errors({
      FORBIDDEN: { status: 403, data: z.object({ message: z.string() }) },
      NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      INTERNAL_SERVER_ERROR: {
        status: 500,
        data: z.object({ message: z.string() }),
      },
    }),
  getApplicationRepositories: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/{applicationId}/repos',
      summary: 'Get application repositories',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: ApplicationsIdSchema }))
    .output(GitRepositoriesReturnSchema),
  getApplicationRepositoriesIntegrated: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/{applicationId}/repos/integrated',
      summary:
        'Get application repositories with cursor pagination and optional search',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationsIdSchema,
        query: z.object({
          gitIntegrationId: z
            .string()
            .optional()
            .describe('Filter repositories by a specific git integration ID'),
          search: z
            .string()
            .trim()
            .min(1)
            .max(200)
            .optional()
            .describe('Search repositories by name'),
          cursor: z
            .string()
            .optional()
            .describe('Opaque pagination cursor from a previous response'),
        }),
      }),
    )
    .output(GitRepositoriesPagedReturnSchema),
  getRepositories: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({ method: 'POST', path: '/repos', inputStructure: 'detailed' })
    .input(z.object({ body: GitRepositoriesRequestSchema }))
    .output(GitRepositoriesReturnSchema),
  getRepositoryByName: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/{applicationId}/repos/by-name/{repositoryName}',
      summary: 'Get repository by name',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: ApplicationRepositoryByNameSchema }))
    .output(GitRepositoryByNameReturnSchema)
    .errors({
      NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      INTERNAL_SERVER_ERROR: {
        status: 500,
        data: z.object({ message: z.string() }),
      },
    }),
  validateGitIntegration: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/{applicationId}/git-integration/validate',
      summary: 'Validate all git integrations for an application',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: ApplicationsIdSchema }))
    .output(
      z.object({
        results: z.array(
          z.object({
            integrationId: z.string(),
            isValid: z.boolean(),
            unreachableRepositoryNames: z.array(z.string()),
          }),
        ),
      }),
    ),
  getApplicationGitIntegrations: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/{applicationId}/git-integrations',
      summary: 'Get all git integrations for an application',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: ApplicationsIdSchema }))
    .output(GitIntegrationsReturnSchema),
  updateApplicationGitIntegration: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'PATCH',
      path: '/{applicationId}/git-integration/{integrationId}',
      summary: 'Update application git integration repositories',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationGitIntegrationIdSchema,
        body: GitIntegrationUpsertBodySchema,
      }),
    )
    .output(GitIntegrationUpsertReturnSchema),
  deleteApplicationGitIntegration: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'DELETE',
      path: '/{applicationId}/git-integration/{integrationId}',
      summary: 'Delete application git integration',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: ApplicationGitIntegrationIdSchema }))
    .output(
      z.object({
        success: z.boolean(),
      }),
    ),
  indexRepository: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/{applicationId}/index-repository',
      summary: 'Index application repository by id',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationsIdSchema,
        body: z.object({
          repositoryId: z.number(),
        }),
      }),
    )
    .output(
      z.object({
        message: z.string(),
      }),
    ),
  verifyPersonalToken: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/verify-personal-token',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        body: z.object({
          platform: GitIntegrationPlatformSchema,
          personalToken: z.string(),
          baseUrl: z
            .string()
            .optional()
            .transform((value) => normalizeGitIntegrationBaseUrl(value)),
          twingateNetworkId: z.string().nullish(),
          internalAccessNetworkId: z.string().nullish(),
        }),
      }),
    )
    .output(
      z.object({
        success: z.boolean(),
        hasWebhookWritePermission: z.boolean(),
        message: z.string().optional(),
        errorType: z
          .enum(['INVALID_CREDENTIALS', 'INVALID_PERMISSIONS'])
          .optional(),
      }),
    ),
  getOrganizations: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/organizations',
      summary: 'Get organizations for authenticated user (OAuth platforms)',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: GitOrganizationsRequestSchema }))
    .output(GitOrganizationsReturnSchema)
    .errors({
      BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
    }),
  verifyManualWebhook: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/git-integration/manual-webhook/verify',
      summary: 'Verify manually configured webhooks for selected repositories',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: ManualWebhookVerificationSchema }))
    .output(ManualWebhookVerificationResultSchema),
  getApplicationWebhookData: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/{applicationId}/git-integration/webhook-data',
      summary: 'Get expected webhook URL for application git integration',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: ApplicationsIdSchema }))
    .output(ApplicationWebhookUrlReturnSchema),
  getBranches: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({ method: 'POST', path: '/branches', inputStructure: 'detailed' })
    .input(z.object({ body: GitBranchesRequestSchema }))
    .output(GitBranchesReturnSchema),
  getBranchesIntegrated: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/{applicationId}/branches/integrated',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationsIdSchema,
        body: GitBranchesRequestSchema.pick({
          repository: true,
          filter: true,
        }),
      }),
    )
    .output(GitBranchesReturnSchema),
  stopIndexing: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/{applicationId}/stop-indexing',
      summary: 'Stop the repository indexing process',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: ApplicationsIdSchema, body: z.object({}) }))
    .output(
      z.object({
        message: z.string(),
      }),
    ),
  getAggregatedData: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/metrics/aggregated',
      summary: 'Get aggregated git metrics data',
      inputStructure: 'detailed',
    })
    .output(GitAggregatedMetricsSchema),
  getApplicationMetrics: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/{applicationId}/metrics',
      summary:
        'Get aggregated git metrics for a specific application across all repositories',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: ApplicationsIdSchema }))
    .output(ApplicationMetricsReturnSchema),
  getApplicationEntryPoints: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/{applicationId}/entry-points',
      summary: 'Get application entry points',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationsIdSchema,
        query: GitEntryPointsQuerySchema,
      }),
    )
    .output(GitEntryPointsReturnSchema),
  getApplicationEntryPointsCount: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/{applicationId}/entry-points/count',
      summary: 'Count application entry points',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationsIdSchema,
        query: GitDateRangeQuerySchema,
      }),
    )
    .output(GitCountReturnSchema),
  getApplicationScannedPullRequestsCount: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/{applicationId}/scanned-pull-requests/count',
      summary: 'Count distinct scanned pull requests for an application',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationsIdSchema,
        query: GitDateRangeQuerySchema,
      }),
    )
    .output(GitCountReturnSchema),
  getApplicationProcessedPullRequests: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/{applicationId}/processed-pull-requests',
      summary: 'List processed pull requests for an application',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationsIdSchema,
        query: GitProcessedPullRequestsQuerySchema,
      }),
    )
    .output(GitProcessedPullRequestsReturnSchema),
  getApplicationProcessedPullRequestsCount: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/{applicationId}/processed-pull-requests/count',
      summary: 'Count processed pull requests for an application',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationsIdSchema,
        query: GitProcessedPullRequestsCountQuerySchema,
      }),
    )
    .output(GitCountReturnSchema),
  getApplicationProcessedPullRequestsSignalsCount: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/{applicationId}/processed-pull-requests/signals-count',
      summary:
        'Sum signals created from processed pull requests for an application',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationsIdSchema,
      }),
    )
    .output(GitCountReturnSchema),
  backfillApplicationProcessedPullRequests: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/{applicationId}/processed-pull-requests/backfill',
      summary:
        'Backfill missing processed pull requests for an application from merged PRs',
      inputStructure: 'detailed',
      successStatus: 202,
    })
    .input(z.object({ params: ApplicationsIdSchema, body: z.object({}) }))
    .output(z.object({ message: z.string() })),
  triggerEntryPointsEnhancement: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/{applicationId}/enhance-entry-points',
      summary: 'Manually trigger enhancement of entry points',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationsIdSchema,
        body: z.object({
          entryPointId: z
            .string()
            .optional()
            .describe('When set, enhance only this entry point'),
          pullNumber: z
            .number()
            .int()
            .positive()
            .optional()
            .describe(
              'When set, enhance uninvestigated entry points for this pull request',
            ),
        }),
      }),
    )
    .output(
      z.object({
        message: z.string(),
        enhancedCount: z.number(),
      }),
    ),
  getCodeGraphById: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/code-graph/{graphId}',
      summary: 'Get a code graph by its ID',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ graphId: z.string() }) }))
    .output(CodeGraphWithRelationsSchema),
  ensureWebhook: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/{applicationId}/ensure-webhook',
      summary: 'Ensure a webhook exists for a specific application',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: ApplicationsIdSchema }))
    .output(
      z.object({
        isValid: z.boolean(),
      }),
    ),
  generateCveSecurityTestCases: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/{applicationId}/generate-cve-test-cases',
      summary:
        'Trigger CVE-based security test case generation for an endpoint',
      inputStructure: 'detailed',
      successStatus: 202,
    })
    .input(
      z.object({
        params: ApplicationsIdSchema,
        body: z.object({ endpointId: z.string() }),
      }),
    )
    .output(z.object({ message: z.string() }))
    .errors({
      INTERNAL_SERVER_ERROR: {
        status: 500,
        data: z.object({ message: z.string() }),
      },
    }),
  generateSecurityTestCases: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/{applicationId}/generate-security-test-cases',
      summary: 'Trigger general security test case generation for an endpoint',
      inputStructure: 'detailed',
      successStatus: 202,
    })
    .input(
      z.object({
        params: ApplicationsIdSchema,
        body: z.object({ endpointId: z.string() }),
      }),
    )
    .output(z.object({ message: z.string() }))
    .errors({
      INTERNAL_SERVER_ERROR: {
        status: 500,
        data: z.object({ message: z.string() }),
      },
    }),
  getEndpointFiles: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/{applicationId}/endpoints/{endpointId}/files',
      summary: 'Get files related to a specific endpoint',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationsIdSchema.extend({ endpointId: z.string() }),
      }),
    )
    .output(EndpointFilesReturnSchema),
  getAllApplicationFiles: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/{applicationId}/files',
      summary: 'Get all files related to an application (across all endpoints)',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: ApplicationsIdSchema }))
    .output(EndpointFilesReturnSchema),
  getFileContent: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/{applicationId}/files/{fileId}/content',
      summary: 'Get content of a specific file',
      inputStructure: 'detailed',
    })
    .input(
      z.object({ params: ApplicationsIdSchema.extend({ fileId: z.string() }) }),
    )
    .output(FileContentReturnSchema),
  getEndpointCodeGraph: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/{applicationId}/endpoints/{endpointId}/code-graph',
      summary: 'Get code graph for a specific endpoint',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationsIdSchema.extend({ endpointId: z.string() }),
      }),
    )
    .output(EndpointCodeGraphReturnSchema)
    .errors({ NOT_FOUND: { status: 404, data: EndpointCodeGraphErrorSchema } }),
  replayPullRequest: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/webhooks/replay-pull-request',
      summary: 'Manually replay PR analysis flow for a previously processed PR',
      inputStructure: 'detailed',
      successStatus: 202,
    })
    .input(
      z.object({
        body: z.object({
          platform: GitIntegrationPlatformSchema,
          pullNumber: z.int().positive(),
          repositoryName: z.string().min(1),
          repositoryId: z.number().int(),
          applicationId: z.string().optional(),
        }),
      }),
    )
    .output(
      z.object({ message: z.string(), processedApplications: z.number() }),
    )
    .errors({
      NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      INTERNAL_SERVER_ERROR: {
        status: 500,
        data: z.object({ message: z.string() }),
      },
    }),
  getAiFlowGraph: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/ai-flow-graph/{graphId}',
      summary: 'Get an AI flow graph by its ID',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ graphId: z.string() }) }))
    .output(AiFlowGraphReturnSchema)
    .errors({
      NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
    }),
  getAiFlowNodeCodeGraphs: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/ai-flow-graph/{graphId}/code-graphs',
      summary: 'Get code graphs linked to nodes in an AI flow graph',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ graphId: z.string() }) }))
    .output(AiFlowNodeCodeGraphsReturnSchema)
    .errors({
      NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
    }),
  transformZipIntegration: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/{applicationId}/git-integration/transform',
      summary:
        'Transform a ZIP_UPLOAD application integration into a provider integration',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApplicationsIdSchema,
        body: TransformZipIntegrationBodySchema,
      }),
    )
    .output(TransformZipIntegrationReturnSchema)
    .errors({
      NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      INTERNAL_SERVER_ERROR: {
        status: 500,
        data: z.object({ message: z.string() }),
      },
    }),
});
